import { getPartStlHead } from '$api/client'
import { List } from 'antd'

const NotBookableReason = (title, description) => ({
  title: title,
  description: description,
})

export const machbarkeitsPruefung = async (
  parts_to_be_checked,
  essential_attributes,
  service_name,
  t,
) => {
  // merge attributes to check against
  for (const array of parts_to_be_checked) {
    array.mergedattributes = [...array.attributes, ...array.float_attributes]
  }
  let bookableParts = []
  let notBookableParts = []

  const checkIf3DModelExists = () => {
    return new Promise((resolve, reject) => {
      Promise.allSettled(
        parts_to_be_checked.map((part) => getPartStlHead(part.id)),
      )
        .then((results) => {
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              notBookableParts.push({
                ...parts_to_be_checked[index],
                reason: {
                  title: t('service.not_processable_because.no_3d_model.title'),
                  description: t(
                    'service.not_processable_because.no_3d_model.description',
                  ),
                },
              })
            } else {
              bookableParts.push(parts_to_be_checked[index])
            }
          })

          resolve({ bookableParts, notBookableParts })
        })
        .catch((error) => {
          console.error('Error handling part checks:', error)
          reject(error)
        })
    })
  }

  const check3DModelForOrderPart = async () => {
    let promises = parts_to_be_checked.map((part) => getPartStlHead(part.id))
    const results = await Promise.allSettled(promises)
    return results.map((result, index) => ({
      id: parts_to_be_checked[index].id,
      has3DModel: result.status === 'fulfilled',
    }))
  }

  switch (service_name) {
    case 'Apply Branding':
      await checkIf3DModelExists()
      break
    case 'Apply Texturing':
      await checkIf3DModelExists()
      break
    case 'Order Part': {
      // Check for 3D model existence first
      const modelChecks = await check3DModelForOrderPart()
      parts_to_be_checked.forEach((part) => {
        const modelCheck = modelChecks.find((mc) => mc.id === part.id)
        part.has3DModel = modelCheck.has3DModel
      })

      // Now check feasibility check
      for (const part of parts_to_be_checked) {
        let isFeasible = false
        // 1. Prioritize the new, granular `dwh_materials_fc_check` field.
        if (
          part.dwh_materials_fc_check &&
          typeof part.dwh_materials_fc_check === 'object'
        ) {
          isFeasible = Object.values(part.dwh_materials_fc_check).some(
            (status) => status === true,
          )
        }

        // 2. If the new field isn't present or shows no feasible options, fall back to the old attribute.
        if (!isFeasible) {
          const oldFcAttribute = part.attributes?.find(
            (attr) => attr.key === 'Feasibility Check',
          )
          if (oldFcAttribute && oldFcAttribute.value === 'true') {
            isFeasible = true
          }
        }

        let bookable = isFeasible && part.has3DModel

        if (bookable) {
          bookableParts.push(part)
        } else {
          let titles = []
          let descriptions = []

          if (!isFeasible) {
            titles.push(
              t(
                'service.not_processable_because.feasibility_check_negative.title',
              ),
            )
            descriptions.push(
              t(
                'service.not_processable_because.feasibility_check_negative.description',
              ),
            )
          }
          if (!part.has3DModel) {
            titles.push(t('service.not_processable_because.no_3d_model.title'))
            descriptions.push(
              t('service.not_processable_because.no_3d_model.description'),
            )
          }

          // Join titles and descriptions with a comma if multiple reasons exist
          let title = titles.join(', ')
          let description = (
            <List
              dataSource={descriptions}
              renderItem={(item) => <List.Item>{item}</List.Item>}
            />
          )

          notBookableParts.push({
            ...part,
            reason: NotBookableReason(title, description),
          })
        }
      }
      break
    }
    case 'Generate 3D Model':
      notBookableParts = notBookableParts.map(
        (part) => (part.has3dmodel = false),
      )
      bookableParts = bookableParts.map((part) => (part.has3dmodel = true))
      await checkIf3DModelExists()
      break
    case 'Feasibility Check': // Fall Through
    default: {
      for (const part of parts_to_be_checked) {
        let isBookable = true
        let reasons = []
        const partAttributeKeys = new Set([
          ...part.attributes.map((attr) => attr.key),
          ...part.float_attributes.map((attr) => attr.key),
        ])

        // 1. Check for material requirements if specified by the service.
        if (!part.client_material || !part.client_material.id) {
          isBookable = false
          reasons.push(
            NotBookableReason(
              t('service.not_processable_because.no_material_assigned.title'),
              t(
                'service.not_processable_because.no_material_assigned.description',
              ),
            ),
          )
        } else if (part.client_material.is_archived) {
          isBookable = false
          reasons.push(
            NotBookableReason(
              t(
                'service.not_processable_because.missing_essential_attributes.title',
              ),
              t(
                'service.not_processable_because.missing_essential_attributes.archived_client_material',
                { material: part.client_material.name },
              ),
            ),
          )
        } else if (
          !part.mapped_dwh_material_ids ||
          part.mapped_dwh_material_ids.length === 0
        ) {
          isBookable = false
          reasons.push(
            NotBookableReason(
              t('service.not_processable_because.no_material_mapping.title'),
              t(
                'service.not_processable_because.no_material_mapping.description',
              ),
            ),
          )
        }

        // 2. Check for all OTHER essential attributes.
        const otherEssentialAttributes = essential_attributes.filter(
          (attr) => attr !== 'Material',
        )
        const missingAttributes = otherEssentialAttributes.filter(
          (attr) => !partAttributeKeys.has(attr),
        )

        if (missingAttributes.length > 0) {
          isBookable = false
          reasons.push(
            NotBookableReason(
              t(
                'service.not_processable_because.missing_essential_attributes.title',
              ),
              t(
                'service.not_processable_because.missing_essential_attributes.description',
                {
                  missing_attributes: missingAttributes
                    .map((attr) => `"${attr}"`)
                    .join(', '),
                },
              ),
            ),
          )
        }

        if (isBookable) {
          bookableParts.push(part)
        } else {
          const combinedTitle = reasons.map((r) => r.title).join(' & ')
          const combinedDescription = (
            <List
              dataSource={reasons.map((r) => r.description)}
              renderItem={(item) => <List.Item>{item}</List.Item>}
            />
          )
          notBookableParts.push({
            ...part,
            reason: NotBookableReason(combinedTitle, combinedDescription),
          })
        }
      }
    }
  }
  return {
    bookableParts,
    notBookableParts,
  }
}
